/**
 * useBoardHeaderResize - Hook for resizable multi-row timeline header.
 * Manages drag-to-resize, auto/manual mode, and row visibility.
 * Writes header state into boardLayoutStore.
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { HEADER_ROW_HEIGHT } from '../utils/boardHelpers';
import { loadNumber, loadString, saveString } from '../../lib/dragResizeUtils';
import { useBoardDragListener } from './useBoardDragListener';
import type { BoardLayoutStore } from '../components/boardLayoutStore';

// ============================================
// Types & Constants
// ============================================

export type HeaderRowId = 'jahre' | 'monate' | 'kw' | 'tage';

const HEADER_DRAG_LEVEL_KEY = 'roadmap_2026_header_drag_level';
const HEADER_MODE_KEY = 'roadmap_2026_header_mode';
const HEADER_MANUAL_ROWS_KEY = 'roadmap_2026_header_manual_rows';
const DAYS_AUTO_HIDE_THRESHOLD = 15;

const ROW_ORDER: HeaderRowId[] = ['jahre', 'monate', 'kw', 'tage'];

const SEQUENCE_ROWS: HeaderRowId[][] = [
  ['kw'],
  ['kw', 'tage'],
  ['monate', 'kw', 'tage'],
  ['jahre', 'monate', 'kw', 'tage'],
];

interface ManualOverrides {
  jahre: boolean;
  monate: boolean;
  tage: boolean;
}

export interface UseHeaderResizeResult {
  handleResizeStart: (e: React.MouseEvent) => void;
  mode: 'auto' | 'manual';
  toggleRow: (row: 'jahre' | 'monate' | 'tage') => void;
  setAutoMode: () => void;
  isRowVisible: (row: HeaderRowId) => boolean;
}

// ============================================
// localStorage helpers
// ============================================

function loadManualOverrides(): ManualOverrides {
  try {
    const stored = localStorage.getItem(HEADER_MANUAL_ROWS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        jahre: !!parsed.jahre,
        monate: !!parsed.monate,
        tage: !!parsed.tage,
      };
    }
  } catch {}
  return { jahre: false, monate: false, tage: false };
}

// ============================================
// Initial state for store init
// ============================================

export function getInitialHeaderState(dayWidth: number) {
  const dragLevel = loadNumber(HEADER_DRAG_LEVEL_KEY, 0, 0, 3);
  const mode = loadString(HEADER_MODE_KEY, 'auto' as const, ['auto', 'manual']);
  const manualOverrides = loadManualOverrides();

  let visibleRows: HeaderRowId[];
  if (mode === 'auto') {
    const base = SEQUENCE_ROWS[dragLevel];
    visibleRows = dayWidth <= DAYS_AUTO_HIDE_THRESHOLD ? base.filter(r => r !== 'tage') : base;
  } else {
    const set = new Set<HeaderRowId>(['kw']);
    if (manualOverrides.tage && dayWidth > DAYS_AUTO_HIDE_THRESHOLD) set.add('tage');
    if (manualOverrides.monate) set.add('monate');
    if (manualOverrides.jahre) set.add('jahre');
    visibleRows = ROW_ORDER.filter(r => set.has(r));
  }

  return {
    visibleRows,
    isHeaderResizing: false,
    headerMode: mode,
    headerHeight: visibleRows.length * HEADER_ROW_HEIGHT,
  };
}

// ============================================
// Hook
// ============================================

export function useBoardHeaderResize(dayWidth: number, store: BoardLayoutStore): UseHeaderResizeResult {
  const [dragLevel, setDragLevel] = useState(() => loadNumber(HEADER_DRAG_LEVEL_KEY, 0, 0, 3));
  const [mode, setMode] = useState<'auto' | 'manual'>(() => loadString(HEADER_MODE_KEY, 'auto', ['auto', 'manual']));
  const [manualOverrides, setManualOverrides] = useState<ManualOverrides>(loadManualOverrides);

  const startYRef = useRef(0);
  const startHeightRef = useRef(0);
  const dragLevelRef = useRef(dragLevel);
  const modeRef = useRef(mode);

  // Keep refs in sync with state
  useEffect(() => { dragLevelRef.current = dragLevel; }, [dragLevel]);
  useEffect(() => { modeRef.current = mode; }, [mode]);

  // Persist state (skip localStorage writes during active resize via isDragging check)
  const isDraggingRef = useRef(false);

  useEffect(() => {
    if (isDraggingRef.current) return;
    saveString(HEADER_DRAG_LEVEL_KEY, String(dragLevel));
  }, [dragLevel]);

  useEffect(() => {
    saveString(HEADER_MODE_KEY, mode);
  }, [mode]);

  useEffect(() => {
    saveString(HEADER_MANUAL_ROWS_KEY, JSON.stringify(manualOverrides));
  }, [manualOverrides]);

  // Compute visible rows
  const visibleRows = useMemo((): HeaderRowId[] => {
    if (mode === 'auto') {
      const base = SEQUENCE_ROWS[dragLevel];
      if (dayWidth <= DAYS_AUTO_HIDE_THRESHOLD) {
        return base.filter(r => r !== 'tage');
      }
      return base;
    }

    // Manual mode
    const set = new Set<HeaderRowId>(['kw']);
    if (manualOverrides.tage && dayWidth > DAYS_AUTO_HIDE_THRESHOLD) set.add('tage');
    if (manualOverrides.monate) set.add('monate');
    if (manualOverrides.jahre) set.add('jahre');
    return ROW_ORDER.filter(r => set.has(r));
  }, [mode, dragLevel, manualOverrides, dayWidth]);

  const headerHeight = visibleRows.length * HEADER_ROW_HEIGHT;

  // Sync to store whenever computed values change
  useEffect(() => {
    store.send({
      type: 'header.config.update',
      visibleRows,
      headerHeight,
      headerMode: mode,
    });
  }, [store, visibleRows, headerHeight, mode]);

  // Drag handlers
  const handleMove = (e: MouseEvent) => {
    const deltaY = e.clientY - startYRef.current;
    const rawHeight = startHeightRef.current + deltaY;
    const currentLevel = dragLevelRef.current;
    const HYSTERESIS = 4;

    const idealLevel = Math.max(0, Math.min(3, Math.round(rawHeight / HEADER_ROW_HEIGHT) - 1));

    if (idealLevel !== currentLevel) {
      let shouldSnap = false;
      if (idealLevel > currentLevel) {
        const boundary = (currentLevel + 1.5) * HEADER_ROW_HEIGHT;
        shouldSnap = rawHeight >= boundary + HYSTERESIS;
      } else {
        const boundary = (currentLevel + 0.5) * HEADER_ROW_HEIGHT;
        shouldSnap = rawHeight <= boundary - HYSTERESIS;
      }

      if (shouldSnap) {
        dragLevelRef.current = idealLevel;
        setDragLevel(idealLevel);
      }
    }

    if (modeRef.current === 'manual') {
      setMode('auto');
    }
  };

  const handleEnd = () => {
    isDraggingRef.current = false;
    saveString(HEADER_DRAG_LEVEL_KEY, String(dragLevelRef.current));
    store.send({ type: 'header.config.update', isHeaderResizing: false });
  };

  const { isDragging: isResizing, startDrag } = useBoardDragListener('row-resize', handleMove, handleEnd);

  // Sync isResizing to store
  useEffect(() => {
    store.send({ type: 'header.config.update', isHeaderResizing: isResizing });
  }, [store, isResizing]);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    startYRef.current = e.clientY;
    startHeightRef.current = visibleRows.length * HEADER_ROW_HEIGHT;
    isDraggingRef.current = true;
    startDrag();
  }, [visibleRows.length, startDrag]);

  const toggleRow = useCallback((row: 'jahre' | 'monate' | 'tage') => {
    setMode('manual');
    setManualOverrides(prev => {
      const currentVisible = new Set(
        mode === 'auto' ? SEQUENCE_ROWS[dragLevel] : ROW_ORDER.filter(r => r === 'kw' || prev[r as keyof ManualOverrides])
      );

      const newOverrides: ManualOverrides = {
        jahre: currentVisible.has('jahre'),
        monate: currentVisible.has('monate'),
        tage: currentVisible.has('tage'),
      };
      newOverrides[row] = !newOverrides[row];
      return newOverrides;
    });
  }, [mode, dragLevel]);

  const setAutoMode = useCallback(() => {
    setMode('auto');
  }, []);

  const isRowVisible = useCallback((row: HeaderRowId): boolean => {
    return visibleRows.includes(row);
  }, [visibleRows]);

  return {
    handleResizeStart,
    mode,
    toggleRow,
    setAutoMode,
    isRowVisible,
  };
}
